import { z } from "zod";
import { PUSH_TARGET_PATH_PATTERN } from "@myapp/contract";
import { buildPushTargetUrl } from "../lib/buildPushTargetUrl";
import { PUSH_TARGET_CUSTOM, PUSH_TARGET_NONE, findPushTarget } from "./pushTargets";

/**
 * Mirrors SendPushMessageDtoSchema minus `userIds` (the selection is not a form field).
 *
 * The DTO's single `url` is composed here out of four fields instead of typed by hand: `target`
 * names a catalog entry, `param1`/`param2` carry whatever that entry still needs, and
 * `customUrl` is the escape hatch. `buildPushTargetUrl` collapses them back into the one string
 * that crosses the wire, so the contract is untouched — which is also why the rules the contract
 * states (`^\/[^\s]*$`, 512 characters) are re-checked here against the *built* URL rather than
 * against whatever the user typed.
 */

/**
 * Imported, not restated: the form has to reject the same strings the API will, and the previous
 * hand-copied regex is exactly how the two drifted apart. See the contract for why `\` counts as
 * a delimiter alongside `/`.
 */
const MAX_URL_LENGTH = 512;

/** Anything that would resolve off-site rather than into the app. */
const leavesTheApp = (path: string) => path.startsWith("//") || path.includes("\\");

export const sendPushFormSchema = z
  .object({
    title: z.string().trim().min(1, "Titel ist erforderlich").max(80, "Maximal 80 Zeichen"),
    body: z.string().trim().min(1, "Text ist erforderlich").max(400, "Maximal 400 Zeichen"),
    target: z.string(),
    param1: z.string(),
    param2: z.string(),
    customUrl: z.string(),
  })
  .superRefine((values, ctx) => {
    if (values.target === PUSH_TARGET_NONE) return;

    if (values.target === PUSH_TARGET_CUSTOM) {
      const custom = values.customUrl.trim();
      if (custom === "") {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["customUrl"], message: "Pfad fehlt" });
      } else if (!PUSH_TARGET_PATH_PATTERN.test(custom)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["customUrl"],
          // "muss mit / beginnen" reads as already satisfied to anyone who typed "//host" or
          // "/\host", which is precisely the input that needs to be turned away.
          message: leavesTheApp(custom)
            ? "Pfad führt aus der App heraus — kein // am Anfang und keine Backslashes"
            : "Pfad muss mit / beginnen",
        });
      } else if (custom.length > MAX_URL_LENGTH) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["customUrl"],
          message: `Maximal ${MAX_URL_LENGTH} Zeichen`,
        });
      }
      return;
    }

    const target = findPushTarget(values.target);
    if (!target) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["target"], message: "Ziel ist unbekannt" });
      return;
    }

    let hasEmptyParam = false;
    (target.params ?? []).forEach((param) => {
      if (values[param.field].trim() === "") {
        hasEmptyParam = true;
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [param.field],
          message: param.requiredMessage,
        });
      }
    });
    if (hasEmptyParam) return;

    // A modal target carries its query string into the URL, so the length limit is reachable
    // here even though no single field is anywhere near it.
    const url = buildPushTargetUrl(values);
    if (url && url.length > MAX_URL_LENGTH) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["target"],
        message: `Ziel-Adresse ist länger als ${MAX_URL_LENGTH} Zeichen`,
      });
    }
  });

export type SendPushFormValues = z.infer<typeof sendPushFormSchema>;

export const sendPushFormDefaults: SendPushFormValues = {
  title: "",
  body: "",
  target: PUSH_TARGET_NONE,
  param1: "",
  param2: "",
  customUrl: "",
};
